/**
 * txViz Web UI — Main entry point.
 *
 * Wires together the chain selector, input form, parser, and result display
 * with the txViz verification library.
 */

import { verify, bytesToHex } from '@txviz/core';
import type { VerifyInput, Chain, BitcoinTransactionInput } from '@txviz/core';
import { ValidationFailedError, TxVizError } from '@txviz/core';
import { ethereumPlugin, parseRawEthereumHex } from '@txviz/ethereum';
import { bitcoinPlugin, parseRawBitcoinHex } from '@txviz/bitcoin';
import { solanaPlugin, parseRawSolanaMessage } from '@txviz/solana';

import { initChainSelector } from './components/chain-selector.js';
import { initInputForm } from './components/input-form.js';
import { renderResult, renderDecoded, renderError, hideResults } from './components/result-display.js';
import { getExample } from './components/examples.js';
import { parseJsonWithBigInt } from './parser.js';

// All plugins registered
const plugins = [ethereumPlugin, bitcoinPlugin, solanaPlugin];

// ── Input Mode Detection ─────────────────────────────────────

type InputMode = 'json' | 'hex' | 'base58';

/**
 * Detect whether the user input is JSON, raw hex, or base58.
 */
function detectInputMode(input: string): InputMode {
  const trimmed = input.trim();

  // JSON: starts with { or [
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return 'json';
  }

  // Hex: starts with 0x
  if (trimmed.startsWith('0x')) {
    return 'hex';
  }

  // Pure hex without 0x prefix (all hex chars, even length, reasonably long)
  if (/^[0-9a-fA-F]+$/.test(trimmed) && trimmed.length % 2 === 0 && trimmed.length > 40) {
    return 'hex';
  }

  // Base58 (Solana messages): alphanumeric with base58 charset, no 0x prefix
  if (/^[1-9A-HJ-NP-Za-km-z]+$/.test(trimmed) && trimmed.length > 40) {
    return 'base58';
  }

  // Default: try JSON
  return 'json';
}

function getInputModeLabel(mode: InputMode, chain: Chain): string {
  switch (mode) {
    case 'json': return 'JSON';
    case 'hex': return chain === 'ethereum' ? 'RLP Hex' : 'Raw Hex';
    case 'base58': return 'Base58';
  }
}

// ── Initialize components ────────────────────────────────────

const chainSelector = initChainSelector((_chain) => {
  hideResults();
  updateModeIndicator();
  updateChainPanels();
  fetchedUtxoData = null;
});

const form = initInputForm();

// ── Real-time input mode indicator ───────────────────────────

const modeIndicator = document.getElementById('input-mode-indicator');
const txTextarea = document.getElementById('tx-json') as HTMLTextAreaElement;

function updateModeIndicator(): void {
  const text = txTextarea.value.trim();
  if (!text || !modeIndicator) {
    if (modeIndicator) modeIndicator.textContent = '';
    return;
  }
  const mode = detectInputMode(text);
  const chain = chainSelector.getActiveChain();
  modeIndicator.textContent = `Detected: ${getInputModeLabel(mode, chain)}`;
}

txTextarea.addEventListener('input', () => {
  updateModeIndicator();
  updateChainPanels();
  // Clear fetched UTXO data when input changes
  fetchedUtxoData = null;
});

// ── UTXO Panel (Bitcoin sighash support) ─────────────────────

const utxoPanel = document.getElementById('utxo-panel')!;
const utxoStatus = document.getElementById('utxo-status')!;
let fetchedUtxoData: Map<string, { value: bigint; scriptPubKey: string }> | null = null;

/**
 * Show/hide chain-specific panels:
 * - Bitcoin: UTXO fetch panel
 * - Solana: hide hash field (not needed — message bytes = what you sign)
 */
const solanaInfo = document.getElementById('solana-info')!;
const hashGroup = document.getElementById('hash-group')!;

function isSolanaChain(): boolean {
  return chainSelector.getActiveChain() === 'solana';
}

function updateChainPanels(): void {
  const chain = chainSelector.getActiveChain();
  const text = txTextarea.value.trim();
  const mode = detectInputMode(text);

  // Bitcoin UTXO panel
  if (chain === 'bitcoin' && mode !== 'json' && text.length > 20) {
    utxoPanel.classList.remove('hidden');
  } else {
    utxoPanel.classList.add('hidden');
    utxoStatus.textContent = '';
    utxoStatus.className = 'utxo-status';
  }

  // Solana: hide hash field, update button text
  const verifyBtn = document.getElementById('verify-btn')!;
  if (chain === 'solana') {
    hashGroup.classList.add('hidden');
    solanaInfo.classList.remove('hidden');
    verifyBtn.textContent = 'Decode Transaction';
  } else {
    hashGroup.classList.remove('hidden');
    solanaInfo.classList.add('hidden');
    verifyBtn.textContent = 'Verify Transaction';
  }
}

// Run once on load to set initial panel visibility
updateChainPanels();

/**
 * Fetch UTXO data from mempool.space API for each input in the transaction.
 */
document.getElementById('fetch-utxo-btn')!.addEventListener('click', async () => {
  const text = txTextarea.value.trim();
  if (!text) return;

  utxoStatus.textContent = 'Fetching UTXO data...';
  utxoStatus.className = 'utxo-status';

  try {
    const tx = parseRawBitcoinHex(text);
    const utxoMap = new Map<string, { value: bigint; scriptPubKey: string }>();

    for (let i = 0; i < tx.inputs.length; i++) {
      const input = tx.inputs[i]!;
      const key = `${input.txid}:${input.vout}`;

      utxoStatus.textContent = `Fetching UTXO ${i + 1}/${tx.inputs.length}: ${input.txid.slice(0, 12)}...:${input.vout}`;

      // mempool.space API: get transaction outputs
      const response = await fetch(
        `https://mempool.space/api/tx/${input.txid}`,
        { signal: AbortSignal.timeout(10000) },
      );

      if (!response.ok) {
        throw new Error(`mempool.space returned ${response.status} for txid ${input.txid}`);
      }

      const txData = await response.json() as {
        vout?: { value: number; scriptpubkey: string }[];
      };

      const output = txData.vout?.[input.vout];
      if (!output) {
        throw new Error(`Output index ${input.vout} not found in tx ${input.txid}`);
      }

      utxoMap.set(key, {
        value: BigInt(output.value),
        scriptPubKey: output.scriptpubkey,
      });
    }

    fetchedUtxoData = utxoMap;

    // Show what was fetched
    const lines: string[] = ['Fetched UTXO data:'];
    for (const [key, data] of utxoMap) {
      lines.push(`  ${key}`);
      lines.push(`    value: ${data.value} sat`);
      lines.push(`    scriptPubKey: ${data.scriptPubKey}`);
    }
    utxoStatus.textContent = lines.join('\n');
    utxoStatus.className = 'utxo-status success';
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    utxoStatus.textContent = `Failed to fetch UTXO data: ${msg}`;
    utxoStatus.className = 'utxo-status error';
    fetchedUtxoData = null;
  }
});

/**
 * Enrich a parsed Bitcoin transaction with fetched UTXO data.
 * Injects prevScriptPubKey and value into each input.
 */
function enrichWithUtxoData(tx: BitcoinTransactionInput): BitcoinTransactionInput {
  if (!fetchedUtxoData || fetchedUtxoData.size === 0) return tx;

  const enrichedInputs = tx.inputs.map((input) => {
    const key = `${input.txid}:${input.vout}`;
    const utxo = fetchedUtxoData!.get(key);
    if (utxo) {
      return {
        ...input,
        value: utxo.value,
        prevScriptPubKey: utxo.scriptPubKey,
      };
    }
    return input;
  });

  return { ...tx, inputs: enrichedInputs };
}

// ── Load Example button ──────────────────────────────────────

document.getElementById('load-example')!.addEventListener('click', async () => {
  const chain = chainSelector.getActiveChain();
  const example = getExample(chain);

  // For Bitcoin, compute the hash asynchronously (Web Crypto SHA-256 is async)
  if (chain === 'bitcoin' && example.expectedHash === '0x' + '00'.repeat(32)) {
    try {
      const txData = parseJsonWithBigInt(example.json, 'bitcoin') as Record<string, unknown>;
      const { serializeBitcoinTransaction } = await import('@txviz/bitcoin');
      const serialized = serializeBitcoinTransaction(txData as any);
      const first = await crypto.subtle.digest('SHA-256', serialized as ArrayBufferView<ArrayBuffer>);
      const second = await crypto.subtle.digest('SHA-256', first);
      const hashBytes = new Uint8Array(second);
      // Reverse for TXID byte order
      const reversed = new Uint8Array(hashBytes.length);
      for (let i = 0; i < hashBytes.length; i++) {
        reversed[i] = hashBytes[hashBytes.length - 1 - i]!;
      }
      example.expectedHash = bytesToHex(reversed);
    } catch (err) {
      console.error('Failed to compute Bitcoin example hash:', err);
    }
  }

  form.setState({
    jsonText: example.json,
    expectedHash: example.expectedHash,
  });

  hideResults();
  updateModeIndicator();
});

// ── Clear button ─────────────────────────────────────────────

document.getElementById('clear-btn')!.addEventListener('click', () => {
  form.clear();
  hideResults();
  updateModeIndicator();
  updateChainPanels();
  fetchedUtxoData = null;
});

// ── Verify button ────────────────────────────────────────────

document.getElementById('verify-btn')!.addEventListener('click', async () => {
  const state = form.getState();
  const solanaRaw = isSolanaChain();

  // Basic client-side validation
  if (!state.jsonText) {
    renderError('Missing Input', 'Please paste the transaction data (JSON or raw hex).');
    return;
  }

  // Hash is required for Ethereum/Bitcoin, but NOT for Solana raw mode
  if (!solanaRaw) {
    if (!state.expectedHash) {
      renderError('Missing Hash', 'Please enter the expected hash you are being asked to sign.');
      return;
    }
    if (!state.expectedHash.startsWith('0x')) {
      renderError('Invalid Hash', 'Expected hash must start with "0x".');
      return;
    }
  }

  form.setLoading(true);
  hideResults();

  try {
    const chain = chainSelector.getActiveChain();
    const inputMode = detectInputMode(state.jsonText);

    let parsed: unknown;
    let expectedHash = state.expectedHash;

    if (inputMode === 'json') {
      // ── JSON path ──────────────────────────────────────────
      try {
        parsed = parseJsonWithBigInt(state.jsonText, chain);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown parse error';
        renderError('Invalid JSON', 'Could not parse the transaction JSON.', msg);
        return;
      }
    } else {
      // ── Raw hex / base58 path ──────────────────────────────
      try {
        const rawInput = state.jsonText.trim();
        switch (chain) {
          case 'ethereum':
            parsed = parseRawEthereumHex(rawInput);
            break;
          case 'bitcoin':
            parsed = parseRawBitcoinHex(rawInput);
            // Enrich with fetched UTXO data (for sighash computation)
            parsed = enrichWithUtxoData(parsed as BitcoinTransactionInput);
            break;
          case 'solana': {
            if (solanaRaw) {
              // Solana decode-only mode: just decode and display, no hash verification
              const solanaTx = parseRawSolanaMessage(rawInput);
              const decoded = await solanaPlugin.decode(solanaTx);
              renderDecoded(decoded);
              return;
            }
            parsed = parseRawSolanaMessage(rawInput);
            break;
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown parse error';
        renderError(
          'Invalid Raw Transaction',
          'Could not parse the raw transaction data. Make sure you selected the correct chain.',
          msg,
        );
        return;
      }
    }

    // Build VerifyInput
    const input = buildVerifyInput(chain, parsed, expectedHash);

    // Run verification
    const result = await verify(input, { plugins });

    // Render result
    renderResult(result);

  } catch (err) {
    if (err instanceof ValidationFailedError) {
      const details = err.errors
        .map((e) => `${e.field}: ${e.message}`)
        .join('\n');
      renderError('Validation Failed', 'The transaction data contains errors.', details);
    } else if (err instanceof TxVizError) {
      renderError('Verification Error', err.message);
    } else {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      renderError('Unexpected Error', 'Something went wrong during verification.', msg);
    }
  } finally {
    form.setLoading(false);
  }
});

// ── Also allow Enter key in hash input ───────────────────────

document.getElementById('expected-hash')!.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    document.getElementById('verify-btn')!.click();
  }
});

// ── Build VerifyInput from parsed data ───────────────────────

function buildVerifyInput(chain: Chain, parsed: unknown, expectedHash: string): VerifyInput {
  switch (chain) {
    case 'ethereum': {
      const obj = parsed as Record<string, unknown>;
      // Detect EIP-712 typed data
      if ('primaryType' in obj && 'domain' in obj && 'types' in obj) {
        return {
          chain: 'ethereum',
          typedData: obj as any,
          expectedHash,
        };
      }
      return {
        chain: 'ethereum',
        transaction: obj as any,
        expectedHash,
      };
    }
    case 'bitcoin':
      return {
        chain: 'bitcoin',
        transaction: parsed as any,
        expectedHash,
      };
    case 'solana':
      return {
        chain: 'solana',
        transaction: parsed as any,
        expectedHash,
      };
  }
}
